import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { API_BASE_URL } from '../../../config/config';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom iOS-style Toggle Switch
const IOSSwitch = styled(Switch)(({ theme }) => ({
  width: 56,
  height: 28,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(28px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#34c759',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 24,
    height: 24,
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 28 / 2,
    backgroundColor: '#e9e9ea',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
    },
  },
}));

const VirtualMotorStatusBox = ({ motor, deviceCode, onRefresh, hasCommError }) => {
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Calculate time display - shows hours and minutes together
  const getTimeDisplay = () => {
    if (!motor.lastCommand?.timestamp) return 'Just now';

    const lastTime = dayjs(motor.lastCommand.timestamp);
    const now = dayjs();
    const totalMinutes = now.diff(lastTime, 'minute');

    if (totalMinutes < 1) return 'Just now';

    const prefix = hasCommError ? 'since' : 'for';

    if (totalMinutes < 60) {
      return `${prefix} ${String(totalMinutes).padStart(2, '0')} Min`;
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${prefix} ${String(hours).padStart(2, '0')} Hr ${String(minutes).padStart(2, '0')} Min`;
    }
  };

  // Proper time and date formatting
  const getDateTimeDisplay = () => {
    if (!motor.lastCommand?.timestamp) return 'Just now';
    // Convert to IST timezone and format properly
    const time = dayjs(motor.lastCommand.timestamp).tz('Asia/Kolkata');
    // Format: 3:28 PM, 18 Oct
    return time.format('h:mm A, D MMM');
  };

  const getRelativeTime = () => {
    if (!motor.lastCommand?.timestamp) return 'just now';
    return dayjs(motor.lastCommand.timestamp).fromNow();
  };

  // Get status color
  const getStatusColor = () => {
    if (motor.sumpError || motor.flowError || motor.pressureError || motor.timeoutError) {
      return '#f44336';
    }
    switch (motor.status) {
      case 'ON': return '#34c759';
      case 'OFF': return '#ff9500';
      case 'PLEASE_WAIT': return '#ff9500';
      default: return '#8e8e93';
    }
  };

  // Handle toggle
  const handleToggle = (checked) => {
    const action = checked ? 'ON' : 'OFF';
    setPendingAction(action);
    setConfirmDialog(true);
  };

  // Confirm action
  const handleConfirm = async () => {
    setConfirmDialog(false);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}api/virtual/motor/${deviceCode}/${motor._id}/execute`,
        { action: pendingAction },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({
        open: true,
        message: `Motor ${pendingAction} command sent successfully`,
        severity: 'success'
      });

      setTimeout(() => {
        if (onRefresh) onRefresh();
      }, 1500);
    } catch (error) {
      console.error('Error toggling motor:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Failed to ${pendingAction} motor`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  const isControlDisabled = loading ||
    motor.sumpError ||
    motor.flowError ||
    motor.pressureError ||
    motor.timeoutError ||
    motor.status === 'PLEASE_WAIT';

  return (
    <>
      <Box
        sx={{
          border: `2px solid ${getStatusColor()}`,
          borderRadius: 2,
          p: 2,
          minWidth: 240,
          maxWidth: 280,
          textAlign: 'center',
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }
        }}
      >
        {/* Motor Name */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1.5,
            color: '#1c1c1e',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {motor.name}
        </Typography>

        {/* Toggle Switch with Status Label */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 1.5
          }}
        >
          {loading ? (
            <CircularProgress size={28} />
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: hasCommError ? '#f44336' : motor.status === 'OFF' ? '#8e8e93' : '#34c759',
                  fontSize: '14px'
                }}
              >
                {hasCommError ? 'N.A.' : motor.status}
              </Typography>
              {!hasCommError && (
                <IOSSwitch
                  checked={motor.status === 'ON'}
                  onChange={(e) => handleToggle(e.target.checked)}
                  disabled={isControlDisabled}
                />
              )}
            </>
          )}
        </Box>

        {/* Time Information */}
        <Typography
          variant="body2"
          sx={{
            fontSize: '13px',
            color: '#3c3c43',
            mb: 0.5
          }}
        >
          {getDateTimeDisplay()}, {getTimeDisplay()}
        </Typography>

        {/* Relative Time */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            color: '#8e8e93'
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontSize: '12px' }}>
            {getRelativeTime()}
          </Typography>
        </Box>

        {/* Error Messages */}
        {(motor.sumpError || motor.flowError || motor.pressureError || motor.timeoutError) && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: '#f44336',
              fontSize: '11px',
              fontWeight: 500
            }}
          >
            {motor.sumpError && 'Sump Error'}
            {motor.flowError && 'Flow Error'}
            {motor.pressureError && 'Pressure Error'}
            {motor.timeoutError && 'Timeout Error'}
          </Typography>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          Confirm Motor {pendingAction}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to turn <strong>{motor.name}</strong> {pendingAction}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialog(false)}
            sx={{
              color: '#8e8e93',
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{
              bgcolor: pendingAction === 'ON' ? '#34c759' : '#ff9500',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: pendingAction === 'ON' ? '#2fb350' : '#e68a00',
              }
            }}
          >
            Confirm {pendingAction}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VirtualMotorStatusBox;
